"use client"

import * as React from "react"
import { Check, ChevronsUpDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface ComboboxProps {
  items: Array<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
  onSelect: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function Combobox({
  items,
  value,
  onChange,
  onSelect,
  placeholder = "Search...",
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setOpen(true);
  };

  // Keep focus when clicking the input
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!open) {
      setOpen(true);
    }
    // Ensure input keeps focus
    inputRef.current?.focus();
  };

  // Prevent losing focus when clicking the button
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    inputRef.current?.focus();
    setOpen(true);
  };

  // Handle popover state changes
  const handleOpenChange = (newOpen: boolean) => {
    // Only allow closing when explicitly clicking outside
    if (!newOpen && document.activeElement !== inputRef.current) {
      setOpen(false);
    }
  };

  // Get the selected item's label
  const selectedLabel = React.useMemo(() => {
    const selectedItem = items.find(item => item.value === value);
    return selectedItem?.label || '';
  }, [items, value]);

  // Filter items based on search term
  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items;
    const search = searchTerm.toLowerCase();
    return items.filter(item => 
      item.label.toLowerCase().includes(search)
    );
  }, [items, searchTerm]);

  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button 
            type="button" 
            className="w-full"
            onClick={handleButtonClick}
          >
            <div className="relative w-full">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={open ? searchTerm : selectedLabel}
                onChange={handleInputChange}
                onClick={handleInputClick}
                onFocus={() => setOpen(true)}
                onBlur={(e) => {
                  // Only close if we're not clicking inside the popover
                  if (!e.relatedTarget?.closest('[role="dialog"]')) {
                    setOpen(false);
                    setSearchTerm("");
                  }
                }}
                className={cn(
                  "w-full h-9",
                  "pl-9 pr-8",
                  "rounded-md border shadow-sm",
                  "bg-background",
                  "placeholder:text-muted-foreground",
                  "focus:outline-none focus:ring-1 focus:ring-ring",
                  "transition-colors duration-200",
                  className
                )}
                placeholder={placeholder}
                autoComplete="off"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronsUpDown 
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-opacity duration-200",
                    open ? "opacity-100" : "opacity-50"
                  )} 
                />
              </div>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0" 
          align="start"
          sideOffset={4}
          forceMount
        >
          <Command 
            className={cn(
              "w-full rounded-lg border shadow-md",
              "bg-background"
            )}
          >
            <CommandList>
              {filteredItems.length === 0 ? (
                <CommandEmpty className="py-6 text-center text-sm">
                  No results found.
                </CommandEmpty>
              ) : (
                <CommandGroup className="max-h-[300px] overflow-auto p-1">
                  {filteredItems.map((item) => (
                    <CommandItem
                      key={item.value}
                      onSelect={() => {
                        onSelect(item.value);
                        setSearchTerm("");
                        setOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer",
                        "hover:bg-accent hover:text-accent-foreground"
                      )}
                    >
                      <span className="flex-1">{item.label}</span>
                      <Check
                        className={cn(
                          "h-4 w-4 text-primary",
                          value === item.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
} 